<template>
  <div id="module-meta">
    <div class="controls">
      <button v-if="!inEditMode" class="btn btn-primary edit-btn" @click="enterEditMode">
        Edit Meta
      </button>
      <button v-if="inEditMode" class="btn btn-success save-btn" @click="updateModuleMeta" :disabled="!validUpdates">
        Save changes
      </button>
      <button v-if="inEditMode" class="btn btn-danger cancel-btn" @click="revertChanges">
        Cancel changes
      </button>
    </div>
    <div v-if="inEditMode">
      <div class="module-title-container">
        <h2>Module Title</h2>
        <k-text-input
          v-model="updates.name"
          :input-placeholder="'Enter new module title'"
          :edit-mode="true"
          :show-reset="true"
          :show-controls="false"
        ></k-text-input>
        <p v-if="titleValidationMessage" class="validation-warning">
          <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
          {{ titleValidationMessage }}
        </p>
      </div>
      <div class="description">
        <h2>Module Description</h2>
        <k-text-editor v-model="updates.description" :maxLength="metaLimits.descriptionMaxLength"></k-text-editor>
      </div>
      <div class="short-description">
        <div class="short-description-heading">
          <h2>Short Description</h2>
          <k-tooltip text="As seen on programme page in KATE" trigger-label="More information about short description">
            <i class="fas fa-info-circle" aria-hidden="true"></i>
          </k-tooltip>
        </div>
        <k-text-editor
          v-model="updates.short_description"
          :maxLength="metaLimits.shortDescriptionMaxLength"
        ></k-text-editor>
      </div>
      <div class="module-tags">
        <word-tags v-model="updates.tags" textInputPlaceholder="Type tag here..."></word-tags>
      </div>
      <div class="learning-outcomes">
        <h2>Learning Outcomes</h2>
        <fieldset class="detail-form form-entry">
          <k-multi-input
            v-if="updates && updates.learning_outcomes"
            v-model="updates.learning_outcomes"
            input-placeholder="Enter new learning outcome"
            empty-message="There are currently no learning outcomes in this module"
            :maxlength="metaLimits.learningOutcomeMaxLength"
            :edit-mode="true"
            :nPlaceholders="0"
          ></k-multi-input>
        </fieldset>
      </div>
    </div>
    <div v-else>
      <div class="meta name">
        <h2>Module Title</h2>
        <p>{{ name }}</p>
      </div>
      <div class="meta description">
        <h2>Description</h2>
        <div v-html="description"></div>
        <p v-if="!description" class="empty">
          Please add a description for this module
        </p>
      </div>
      <div class="meta short-description">
        <div class="short-description-heading">
          <h2>Short Description</h2>
          <k-tooltip text="As seen on programme page in KATE" trigger-label="More information about short description">
            <i class="fas fa-info-circle" aria-hidden="true"></i>
          </k-tooltip>
        </div>
        <div v-html="shortDescription"></div>
        <p v-if="!shortDescription" class="empty">
          Please add a short description for this module. If left empty a truncated version of the description will be used.
        </p>
      </div>
      <div class="meta module-tags">
        <h2>Tags</h2>
        <ul class="k-word-tags-list">
          <li class="k-word-tags-item badge" v-for="tag in tags">{{tag}}</li>
        </ul>
        <p v-if="!tags.length" :class="!tags.length ? 'empty' : ''">
          Please add tags for this module
        </p>
      </div>
      <div class="meta learning-outcomes">
        <h2>Learning Outcomes</h2>
        <ol v-if="learningOutcomes.length">
          <li v-for="lo in learningOutcomes">
            <i class="fas fa-bullseye" aria-hidden="true"></i>
            {{ lo }}
          </li>
        </ol>
        <p v-if="!learningOutcomes.length" :class="!learningOutcomes.length ? 'empty' : ''">
          Please add learning outcomes for this module
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
#module-meta h2 {
  margin: 20px 0;
}

.short-description-heading {
  display: flex;
  align-items: center;
  gap: 6px;
}

.learning-outcomes ol li {
  display: block;
  margin-top: 20px;
}

.learning-outcomes ol li i {
  color: var(--kate-type-accent);
}

.meta p.empty {
  font-style: italic;
}

.validation-warning {
  color: var(--kate-type-warning);
  margin-top: 0.5em;
}

.text-area-array-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.text-area-entry {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
}

.controls button {
  float: right;
  margin-left: 10px;
}

.k-word-tags-item {
  color: var(--kate-type-white);
}

</style>

<script>
import ModuleTypeMixin from '@mixins/module-type-mixin';
import KMultiInput from '@base-components/k-multi-input.vue';
import KTextInput from '@base-components/k-text-input.vue';
import KTextEditor from '@base-components/k-text-editor.vue';
import { KTooltip } from '@kate/components';
import WordTags from '../../../components/word-tags.vue';
import { MODULE_TYPES } from '../../../../constants';

const MODULE_META_LIMITS = {
  module: {
    nameMaxLength: 256,
    descriptionMaxLength: 1024,
    shortDescriptionMaxLength: 180,
    learningOutcomeMaxLength: 512,
  },
  programme_blueprint_module: {
    nameMaxLength: 256,
    descriptionMaxLength: 1024,
    shortDescriptionMaxLength: 256,
    learningOutcomeMaxLength: 256,
  },
  module_blueprint: {
    nameMaxLength: 256,
    descriptionMaxLength: 1024,
    shortDescriptionMaxLength: 256,
    learningOutcomeMaxLength: 256,
  },
};

const DEFAULT_META_LIMITS = MODULE_META_LIMITS.module;

export default {
  components: {
    KTooltip,
    WordTags,
    KTextInput,
    KMultiInput,
    KTextEditor,
  },

  mixins: [ModuleTypeMixin],

  props: {
    module: {
      type: Object,
    },
    moduleType: {
      type: String,
      default: 'module',
      validator: value => Object.keys(MODULE_TYPES).includes(value),
    },
  },

  data() {
    return {
      updates: {
        learning_outcomes: undefined,
        name: undefined,
        description: undefined,
        short_description: undefined,
        tags: undefined,
      },
      inEditMode: false,
    };
  },

  computed: {
    name() {
      return this.updates?.name ?? this.module?.name;
    },
    description() {
      return this.updates?.description ?? this.module?.description;
    },
    shortDescription() {
      return this.updates?.short_description ?? this.module?.short_description;
    },
    tags() {
      return this.updates?.tags ?? this.module?.tags ?? [];
    },
    learningOutcomes() {
      return this.updates?.learning_outcomes ?? this.module?.learning_outcomes ?? [];
    },
    metaLimits() {
      return MODULE_META_LIMITS[this.moduleType] ?? DEFAULT_META_LIMITS;
    },
    titleValidationMessage() {
      const name = this.updates.name;
      if (!name || !name.trim()) {
        return `Please add a title for this ${this.moduleTypeMeta.name.toLowerCase()}.`;
      }
      if (name.length > this.metaLimits.nameMaxLength) {
        return `Title must be at most ${this.metaLimits.nameMaxLength} characters.`;
      }
      return '';
    },
    validTitle() {
      return !this.titleValidationMessage;
    },
    descriptionTooLong() {
      const description = this.updates.description ?? '';
      return description.length > this.metaLimits.descriptionMaxLength;
    },
    validDescription() {
      return !this.descriptionTooLong;
    },
    validShortDescription() {
      const shortDescription = this.updates.short_description;
      if (shortDescription == null || shortDescription === '') {
        return true;
      }
      return shortDescription.length <= this.metaLimits.shortDescriptionMaxLength;
    },
    validLearningOutcomes() {
      const maxLength = this.metaLimits.learningOutcomeMaxLength;
      return this.learningOutcomes.map(el => el && el.length >= 2 && el.length <= maxLength);
    },
    validUpdates() {
      if (!this.updates) {
        return false;
      }
      return this.validTitle
        && this.validDescription
        && this.validShortDescription
        && this.validLearningOutcomes.every(check => check === true);
    },
    metaEndpoint() {
      return `${this.moduleTypeMeta.moduleApiRoot}/${this.module.id}/meta`;
    },
    updatePayload() {
      return {
        name: this.name,
        description: this.description,
        short_description: this.shortDescription,
        learning_outcomes: this.learningOutcomes.filter(lo => lo.trim().length),
        tags: this.tags,
      };
    },
  },

  methods: {
    updateModuleMeta() {
      const logContext = {};
      logContext[this.moduleTypeMeta.idField] = this.id;
      if (this.moduleTypeMeta.programmeIdField) {
        logContext[this.moduleTypeMeta.programmeIdField] = this.programmeId;
      }
      this.$logger.info(`Updating ${this.moduleTypeMeta.name} meta`, {
        ...logContext,
        oldName: this.name,
        newName: this.updatePayload.name,
        changedFields: Object.keys(this.updatePayload).filter(k => this.updatePayload[k] !== this[k]),
      }, true);
      this.$Loading.minimal();
      this.$http.put(this.metaEndpoint, this.updatePayload).then(() => {
        this.$logger.info(`Successfully updated ${this.moduleTypeMeta.name} meta`, logContext, true);
        this.$ktoast.success(`${this.moduleTypeMeta.name} meta updated`);
        this.$emit('metaUpdated');
      }).catch(err => {
        this.$logger.autowarn(`Error updating ${this.moduleTypeMeta.name} meta`, logContext, err);
        this.$ktoast.showError(err);
        this.revertChanges();
      }).then(() => {
        this.$Loading.finish();
        this.inEditMode = false;
      });
    },
    enterEditMode() {
      this.updates.learning_outcomes = this.learningOutcomes;
      this.updates.name = this.name;
      this.updates.description = this.description;
      this.updates.short_description = this.shortDescription;
      this.updates.tags = this.tags;
      this.inEditMode = true;
    },
    revertChanges() {
      this.updates.learning_outcomes = this.module.learning_outcomes;
      this.updates.name = this.module.name;
      this.updates.description = this.module.description;
      this.updates.short_description = this.module.short_description;
      this.updates.tags = this.module.tags;
      this.inEditMode = false;
    },
  },
};
</script>
